// Ship rendering functions - 機体描画の共通処理

import { getP5 } from '../../../core/p5-instance';
import {
    getCannonActive,
    getCannonCharging,
    getGameState,
    getPowerLevel,
    getSelectedShip,
    getShieldActive,
    getPlayer,
    getSideBoosterLeftTimer,
    getSideBoosterRightTimer,
    getBoosterAlpha
} from '../../../store/index';
import { drawBoosterFlame, drawShipEngineFlames } from '../../particles/booster-effects';
import { drawEnergyBarrier } from './shield-effect.js';
import {
    Player
} from '../../../types/game-types';

/**
 * 機体描画のオプション設定
 */
interface ShipOptions {
    showEngine?: boolean;
    showGlow?: boolean;
    moveDirection?: number;  // 移動方向 (-1～1の正規化値)
}

/**
 * エンジンアニメーションパラメータを計算
 * @param moveDirection - 移動方向（targetX - x）
 * @returns 炎のオフセットと左右エンジンの強度乗数
 */
function getEngineAnimParams(moveDirection: number): { flameOffsetX: number; leftMultiplier: number; rightMultiplier: number } {
    const dir = Math.max(-1, Math.min(1, moveDirection / 50));
    return {
        flameOffsetX: -dir * 3,           // 炎の水平オフセット（移動方向と反対）
        leftMultiplier: 1 + dir * 0.25,   // 左エンジン強度
        rightMultiplier: 1 - dir * 0.25   // 右エンジン強度
    };
}

/**
 * drawPlayerEffects() - プレイヤーエフェクトの描画
 * シールド、パワーレベルインジケーターを描画
 * @param size - プレイヤーサイズ
 */
export function drawPlayerEffects(size: number): void {
    const p = getP5();
    const shieldActive = getShieldActive();
    const powerLevel = getPowerLevel();

    // シールドエフェクト
    if (shieldActive) {
        drawEnergyBarrier(size);
    }

    // パワーレベルインジケーター
    if (powerLevel > 1) {
        p.fill(0, 255, 100);
        p.noStroke();
        for (let i = 0; i < powerLevel - 1; i++) {
            const px = (i - (powerLevel - 2) / 2) * 8;
            p.circle(px, size / 4, 4);
        }
    }
}

/**
 * drawShipBomber() - BOMBER機体の描画
 * @param x - X座標
 * @param y - Y座標
 * @param size - 機体サイズ
 * @param options - オプション設定
 */
export function drawShipBomber(x: number, y: number, size: number, options: ShipOptions = {}): void {
    const p = getP5();
    const {
        showEngine = true, showGlow = true, moveDirection = 0
    } = options;

    p.push();
    p.translate(x, y);

    // === 1. エンジン強化 - 4層グラデーション炎 ===
    if (showEngine) {
        const enginePulse = p.sin(p.frameCount * 0.3) * 4 + 8;
        const engineY = size / 2 + 5;
        const { flameOffsetX } = getEngineAnimParams(moveDirection);

        // 外側グロー（最も透明）
        p.fill(0, 150, 255, 60);
        p.noStroke();
        p.ellipse(flameOffsetX, engineY, 18, enginePulse + 14);

        // 中間炎
        p.fill(0, 200, 255, 100);
        p.ellipse(flameOffsetX, engineY, 14, enginePulse + 10);

        // 内側炎
        p.fill(100, 255, 255, 180);
        p.ellipse(flameOffsetX * 0.7, engineY, 10, enginePulse + 6);

        // 白熱コア
        p.fill(255, 255, 255, 255);
        p.ellipse(flameOffsetX * 0.4, engineY, 6, enginePulse);
    }

    // === 2. 船体のグロー（既存） ===
    if (showGlow) {
        p.fill(0, 255, 255, 30);
        p.noStroke();
        p.beginShape();
        p.vertex(0, -size / 2 - 5);
        p.vertex(-size / 2 - 5, size / 2 + 5);
        p.vertex(0, size / 3 + 5);
        p.vertex(size / 2 + 5, size / 2 + 5);
        p.endShape(p.CLOSE);
    }

    // === 3. シャドウ層（新規：船体の下に暗い層） ===
    p.fill(0, 100, 150);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 2 + 2);
    p.vertex(-size / 2, size / 2 + 2);
    p.vertex(0, size / 3 + 2);
    p.vertex(size / 2, size / 2 + 2);
    p.endShape(p.CLOSE);

    // === 4. 船体本体（既存） ===
    p.fill(0, 200, 255);
    p.stroke(0, 255, 255);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(0, -size / 2);
    p.vertex(-size / 2, size / 2);
    p.vertex(0, size / 3);
    p.vertex(size / 2, size / 2);
    p.endShape(p.CLOSE);

    // === 5. ハイライト層（新規：船体上部の明るい三角形） ===
    p.fill(100, 255, 255, 100);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 2);
    p.vertex(-size / 6, -size / 6);
    p.vertex(size / 6, -size / 6);
    p.endShape(p.CLOSE);

    // === 6. パネルライン（新規：機体ディテール） ===
    p.stroke(0, 255, 255, 120);
    p.strokeWeight(1);
    p.line(0, -size / 2, 0, -size / 8);  // 中央縦ライン

    // === 7. コックピット（既存） ===
    p.fill(255, 0, 255);
    p.noStroke();
    p.ellipse(0, 0, size / 4, size / 3);

    // === 8. コックピットハイライト（新規：内側の光沢） ===
    p.fill(255, 150, 255, 150);
    p.ellipse(-size / 16, -size / 12, size / 8, size / 6);

    p.pop();
}

/**
 * drawShipHunter() - HUNTER機体の描画
 * グラデーションとテクスチャで立体感を演出
 * @param x - X座標
 * @param y - Y座標
 * @param size - 機体サイズ
 * @param options - オプション設定
 */
export function drawShipHunter(x: number, y: number, size: number, options: ShipOptions = {}): void {
    const p = getP5();
    const {
        showEngine = true, showGlow = true, moveDirection = 0
    } = options;

    p.push();
    p.translate(x, y);

    // === 1. エンジン強化 - 4層グラデーション炎 ===
    if (showEngine) {
        const enginePulse = p.sin(p.frameCount * 0.3) * 4 + 8;
        const engineY = size / 2 + 3;
        const { flameOffsetX, leftMultiplier, rightMultiplier } = getEngineAnimParams(moveDirection);

        // 左エンジン
        const leftPulse = enginePulse * leftMultiplier;
        // 外側グロー (最も透明)
        p.fill(255, 100, 0, 60);
        p.noStroke();
        p.ellipse(-10 + flameOffsetX, engineY, 14 * leftMultiplier, leftPulse + 12);

        // 中間炎
        p.fill(255, 150, 0, 100);
        p.ellipse(-10 + flameOffsetX * 0.8, engineY, 11 * leftMultiplier, leftPulse + 8);

        // 内側炎
        p.fill(255, 200, 50, 180);
        p.ellipse(-10 + flameOffsetX * 0.5, engineY, 8 * leftMultiplier, leftPulse + 4);

        // 白熱コア
        p.fill(255, 255, 200, 255);
        p.ellipse(-10 + flameOffsetX * 0.3, engineY, 5 * leftMultiplier, leftPulse);

        // 右エンジン
        const rightPulse = enginePulse * rightMultiplier;
        // 外側グロー (最も透明)
        p.fill(255, 100, 0, 60);
        p.ellipse(10 + flameOffsetX, engineY, 14 * rightMultiplier, rightPulse + 12);

        // 中間炎
        p.fill(255, 150, 0, 100);
        p.ellipse(10 + flameOffsetX * 0.8, engineY, 11 * rightMultiplier, rightPulse + 8);

        // 内側炎
        p.fill(255, 200, 50, 180);
        p.ellipse(10 + flameOffsetX * 0.5, engineY, 8 * rightMultiplier, rightPulse + 4);

        // 白熱コア
        p.fill(255, 255, 200, 255);
        p.ellipse(10 + flameOffsetX * 0.3, engineY, 5 * rightMultiplier, rightPulse);
    }

    // === 2. 船体グロー ===
    if (showGlow) {
        p.fill(255, 150, 0, 30);
        p.noStroke();
        p.beginShape();
        p.vertex(0, -size / 2 - 8);
        p.vertex(-size / 2 + 5, size / 2 + 5);
        p.vertex(0, size / 4 + 5);
        p.vertex(size / 2 - 5, size / 2 + 5);
        p.endShape(p.CLOSE);
    }

    // === 3. 船体立体化 - 3層構造 ===

    // シャドウ層 (下方オフセット、暗い)
    p.fill(180, 70, 0);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 2 - 5 + 2);
    p.vertex(-size / 2 + 5, size / 2 + 2);
    p.vertex(0, size / 4 + 2);
    p.vertex(size / 2 - 5, size / 2 + 2);
    p.endShape(p.CLOSE);

    // ベース層 (メイン船体)
    p.fill(255, 120, 0);
    p.stroke(255, 180, 0);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(0, -size / 2 - 5);
    p.vertex(-size / 2 + 5, size / 2);
    p.vertex(0, size / 4);
    p.vertex(size / 2 - 5, size / 2);
    p.endShape(p.CLOSE);

    // ハイライト層 (上部に三角オーバーレイ)
    p.fill(255, 180, 50, 150);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 2 - 5);
    p.vertex(-size / 4, 0);
    p.vertex(0, size / 8);
    p.vertex(size / 4, 0);
    p.endShape(p.CLOSE);

    // === 3.5 船体の左右面分割（光源表現） ===
    // 左半面 (光源側 - 明るい)
    p.fill(255, 150, 30, 100);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 2 - 5);
    p.vertex(-size / 2 + 5, size / 2);
    p.vertex(0, size / 4);
    p.endShape(p.CLOSE);

    // 右半面 (シャドウ側 - 暗い)
    p.fill(140, 50, 0, 80);
    p.beginShape();
    p.vertex(0, -size / 2 - 5);
    p.vertex(0, size / 4);
    p.vertex(size / 2 - 5, size / 2);
    p.endShape(p.CLOSE);

    // === 3.6 船体凸表現 ===
    // 中央の盛り上がりハイライト（凸の頂点が明るい）
    p.fill(255, 200, 100, 120);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 4);
    p.vertex(-size / 8, 0);
    p.vertex(0, size / 8);
    p.vertex(size / 8, 0);
    p.endShape(p.CLOSE);

    // === 4. パネルライン追加 ===
    p.stroke(200, 80, 0, 120);
    p.strokeWeight(1);
    // 中央縦ライン
    p.line(0, -size / 2, 0, -size / 8);
    // 斜めサイドライン (左)
    p.line(-size / 6, -size / 4, -size / 3, size / 6);
    // 斜めサイドライン (右)
    p.line(size / 6, -size / 4, size / 3, size / 6);

    // === 5. 翼強化 - グラデーション + エッジライト ===

    // === 5.0 翼付け根シャドウ ===
    // 左翼付け根の影
    p.fill(80, 20, 0, 100);
    p.noStroke();
    p.beginShape();
    p.vertex(-size / 2 + 5, size / 4);
    p.vertex(-size / 3, size / 4 + 4);
    p.vertex(-size / 4, size / 3 + 2);
    p.endShape(p.CLOSE);

    // 右翼付け根の影
    p.beginShape();
    p.vertex(size / 2 - 5, size / 4);
    p.vertex(size / 3, size / 4 + 4);
    p.vertex(size / 4, size / 3 + 2);
    p.endShape(p.CLOSE);

    // 左翼ベース (暗いオレンジ)
    p.fill(180, 60, 0);
    p.stroke(255, 180, 0);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(-size / 2 + 5, size / 4);
    p.vertex(-size / 2 - 10, size / 2);
    p.vertex(-size / 4, size / 3);
    p.endShape(p.CLOSE);

    // 左翼厚み表現 (下端の暗い帯)
    p.fill(100, 30, 0, 150);
    p.noStroke();
    p.beginShape();
    p.vertex(-size / 2 - 10, size / 2);
    p.vertex(-size / 2 - 8, size / 2 + 3);
    p.vertex(-size / 4 + 2, size / 3 + 3);
    p.vertex(-size / 4, size / 3);
    p.endShape(p.CLOSE);

    // 左翼ハイライト (上端オーバーレイ)
    p.fill(255, 140, 30, 120);
    p.noStroke();
    p.beginShape();
    p.vertex(-size / 2 + 5, size / 4);
    p.vertex(-size / 2 - 5, size / 2 - 3);
    p.vertex(-size / 4 - 2, size / 3 - 2);
    p.endShape(p.CLOSE);

    // 左翼エッジライン (発光)
    p.stroke(255, 200, 100, 150);
    p.strokeWeight(1);
    p.line(-size / 2 + 5, size / 4, -size / 2 - 10, size / 2);

    // 右翼ベース (暗いオレンジ)
    p.fill(180, 60, 0);
    p.stroke(255, 180, 0);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(size / 2 - 5, size / 4);
    p.vertex(size / 2 + 10, size / 2);
    p.vertex(size / 4, size / 3);
    p.endShape(p.CLOSE);

    // 右翼厚み表現 (下端の暗い帯)
    p.fill(100, 30, 0, 150);
    p.noStroke();
    p.beginShape();
    p.vertex(size / 2 + 10, size / 2);
    p.vertex(size / 2 + 8, size / 2 + 3);
    p.vertex(size / 4 - 2, size / 3 + 3);
    p.vertex(size / 4, size / 3);
    p.endShape(p.CLOSE);

    // 右翼ハイライト (上端オーバーレイ)
    p.fill(255, 140, 30, 120);
    p.noStroke();
    p.beginShape();
    p.vertex(size / 2 - 5, size / 4);
    p.vertex(size / 2 + 5, size / 2 - 3);
    p.vertex(size / 4 + 2, size / 3 - 2);
    p.endShape(p.CLOSE);

    // 右翼エッジライン (発光)
    p.stroke(255, 200, 100, 150);
    p.strokeWeight(1);
    p.line(size / 2 - 5, size / 4, size / 2 + 10, size / 2);

    // === 6. エッジハイライト - リムライティング（二重構造） ===
    // 左エッジ外側 (ベースリム)
    p.stroke(255, 180, 50, 120);
    p.strokeWeight(2);
    p.line(0, -size / 2 - 5, -size / 2 + 5, size / 2);

    // 左エッジ内側 (明るいハイライト - ベベル効果)
    p.stroke(255, 240, 150, 200);
    p.strokeWeight(1);
    p.line(1, -size / 2 - 3, -size / 2 + 7, size / 2 - 2);

    // 右エッジ外側 (ベースリム - シャドウ側)
    p.stroke(200, 100, 20, 60);
    p.strokeWeight(2);
    p.line(0, -size / 2 - 5, size / 2 - 5, size / 2);

    // 右エッジ内側 (やや明るい - ベベル効果)
    p.stroke(255, 160, 50, 100);
    p.strokeWeight(1);
    p.line(-1, -size / 2 - 3, size / 2 - 7, size / 2 - 2);

    // === 7. コックピット強化 - ガラス反射 ===
    // リム (フレーム)
    p.fill(200, 150, 0, 200);
    p.noStroke();
    p.ellipse(0, 0, size / 5 + 3, size / 4 + 3);

    // ガラス本体
    p.fill(255, 255, 100);
    p.ellipse(0, 0, size / 5, size / 4);

    // メイン反射 (左上)
    p.fill(255, 255, 255, 200);
    p.ellipse(-size / 20, -size / 16, size / 12, size / 10);

    // サブ反射 (右下)
    p.fill(255, 255, 180, 100);
    p.ellipse(size / 30, size / 20, size / 16, size / 14);

    p.pop();
}

/**
 * drawShipLancer() - LANCER機体の描画
 * グラデーションとテクスチャで立体感を演出
 * @param x - X座標
 * @param y - Y座標
 * @param size - 機体サイズ
 * @param options - オプション設定
 */
export function drawShipLancer(x: number, y: number, size: number, options: ShipOptions = {}): void {
    const p = getP5();
    const {
        showEngine = true, showGlow = true, moveDirection = 0
    } = options;

    p.push();
    p.translate(x, y);

    // === 1. エンジン強化 - 4層グラデーション炎 ===
    if (showEngine) {
        const enginePulse = p.sin(p.frameCount * 0.3) * 4 + 8;
        const engineY = size / 2 + 3;
        const { flameOffsetX, leftMultiplier, rightMultiplier } = getEngineAnimParams(moveDirection);

        // 左エンジン
        const leftPulse = enginePulse * leftMultiplier;
        // 外側グロー (最も透明)
        p.fill(200, 50, 255, 60);
        p.noStroke();
        p.ellipse(-size / 4 + flameOffsetX, engineY, 14 * leftMultiplier, leftPulse + 12);

        // 中間炎
        p.fill(220, 100, 255, 100);
        p.ellipse(-size / 4 + flameOffsetX * 0.8, engineY, 11 * leftMultiplier, leftPulse + 8);

        // 内側炎
        p.fill(240, 150, 255, 180);
        p.ellipse(-size / 4 + flameOffsetX * 0.5, engineY, 8 * leftMultiplier, leftPulse + 4);

        // 白熱コア
        p.fill(255, 220, 255, 255);
        p.ellipse(-size / 4 + flameOffsetX * 0.3, engineY, 5 * leftMultiplier, leftPulse);

        // 右エンジン
        const rightPulse = enginePulse * rightMultiplier;
        // 外側グロー (最も透明)
        p.fill(200, 50, 255, 60);
        p.ellipse(size / 4 + flameOffsetX, engineY, 14 * rightMultiplier, rightPulse + 12);

        // 中間炎
        p.fill(220, 100, 255, 100);
        p.ellipse(size / 4 + flameOffsetX * 0.8, engineY, 11 * rightMultiplier, rightPulse + 8);

        // 内側炎
        p.fill(240, 150, 255, 180);
        p.ellipse(size / 4 + flameOffsetX * 0.5, engineY, 8 * rightMultiplier, rightPulse + 4);

        // 白熱コア
        p.fill(255, 220, 255, 255);
        p.ellipse(size / 4 + flameOffsetX * 0.3, engineY, 5 * rightMultiplier, rightPulse);
    }

    // === 2. 船体グロー ===
    if (showGlow) {
        p.fill(200, 50, 255, 30);
        p.noStroke();
        p.beginShape();
        p.vertex(-size / 4, -size / 2 - 8);
        p.vertex(-size / 3 - 5, size / 2 + 5);
        p.vertex(size / 3 + 5, size / 2 + 5);
        p.vertex(size / 4, -size / 2 - 8);
        p.endShape(p.CLOSE);
    }

    // === 3. 砲身立体化 - 3層構造 ===

    // 左砲身シャドウ層 (下方オフセット、暗い)
    p.fill(80, 15, 120);
    p.noStroke();
    p.beginShape();
    p.vertex(-size / 4, -size / 2 - 5 + 2);
    p.vertex(-size / 3, 0 + 2);
    p.vertex(-size / 6, size / 3 + 2);
    p.vertex(-size / 8, 0 + 2);
    p.endShape(p.CLOSE);

    // 右砲身シャドウ層
    p.beginShape();
    p.vertex(size / 4, -size / 2 - 5 + 2);
    p.vertex(size / 8, 0 + 2);
    p.vertex(size / 6, size / 3 + 2);
    p.vertex(size / 3, 0 + 2);
    p.endShape(p.CLOSE);

    // 左砲身ベース層
    p.fill(120, 30, 180);
    p.stroke(200, 80, 255);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(-size / 4, -size / 2 - 5);
    p.vertex(-size / 3, 0);
    p.vertex(-size / 6, size / 3);
    p.vertex(-size / 8, 0);
    p.endShape(p.CLOSE);

    // 右砲身ベース層
    p.beginShape();
    p.vertex(size / 4, -size / 2 - 5);
    p.vertex(size / 8, 0);
    p.vertex(size / 6, size / 3);
    p.vertex(size / 3, 0);
    p.endShape(p.CLOSE);

    // 左砲身ハイライト層 (光源側 - 内側面)
    p.fill(180, 80, 240, 120);
    p.noStroke();
    p.beginShape();
    p.vertex(-size / 4, -size / 2 - 5);
    p.vertex(-size / 8, 0);
    p.vertex(-size / 6, size / 6);
    p.vertex(-size / 5, -size / 6);
    p.endShape(p.CLOSE);

    // 右砲身ハイライト層 (光源側 - 内側面)
    p.beginShape();
    p.vertex(size / 4, -size / 2 - 5);
    p.vertex(size / 5, -size / 6);
    p.vertex(size / 6, size / 6);
    p.vertex(size / 8, 0);
    p.endShape(p.CLOSE);

    // 左砲身外側シャドウ (暗い面)
    p.fill(70, 15, 100, 80);
    p.beginShape();
    p.vertex(-size / 4, -size / 2 - 5);
    p.vertex(-size / 3, 0);
    p.vertex(-size / 6 - 2, size / 6);
    p.vertex(-size / 5, -size / 6);
    p.endShape(p.CLOSE);

    // 右砲身外側シャドウ (暗い面)
    p.beginShape();
    p.vertex(size / 4, -size / 2 - 5);
    p.vertex(size / 5, -size / 6);
    p.vertex(size / 6 + 2, size / 6);
    p.vertex(size / 3, 0);
    p.endShape(p.CLOSE);

    // === 4. パネルライン追加 ===
    p.stroke(150, 50, 200, 120);
    p.strokeWeight(1);
    // 左砲身中央ライン
    p.line(-size / 4, -size / 2 - 5, -size / 5, size / 6);
    // 右砲身中央ライン
    p.line(size / 4, -size / 2 - 5, size / 5, size / 6);

    // === 5. 中央ボディ立体化 ===

    // 中央ボディシャドウ層
    p.fill(100, 20, 130);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 4 + 2);
    p.vertex(-size / 5, size / 3 + 2);
    p.vertex(0, size / 2 + 2);
    p.vertex(size / 5, size / 3 + 2);
    p.endShape(p.CLOSE);

    // 中央ボディベース層
    p.fill(160, 40, 200);
    p.stroke(200, 80, 255);
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(0, -size / 4);
    p.vertex(-size / 5, size / 3);
    p.vertex(0, size / 2);
    p.vertex(size / 5, size / 3);
    p.endShape(p.CLOSE);

    // 中央ボディハイライト層
    p.fill(200, 100, 255, 100);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 4);
    p.vertex(-size / 10, size / 8);
    p.vertex(0, size / 4);
    p.vertex(size / 10, size / 8);
    p.endShape(p.CLOSE);

    // 中央ボディ左面 (光源側)
    p.fill(190, 80, 240, 80);
    p.beginShape();
    p.vertex(0, -size / 4);
    p.vertex(-size / 5, size / 3);
    p.vertex(0, size / 2);
    p.endShape(p.CLOSE);

    // 中央ボディ右面 (シャドウ側)
    p.fill(90, 20, 120, 60);
    p.beginShape();
    p.vertex(0, -size / 4);
    p.vertex(0, size / 2);
    p.vertex(size / 5, size / 3);
    p.endShape(p.CLOSE);

    // === 6. エッジハイライト - リムライティング ===
    // 左砲身エッジ外側 (ベースリム)
    p.stroke(200, 100, 255, 120);
    p.strokeWeight(2);
    p.line(-size / 4, -size / 2 - 5, -size / 3, 0);

    // 左砲身エッジ内側 (明るいハイライト - ベベル効果)
    p.stroke(240, 180, 255, 200);
    p.strokeWeight(1);
    p.line(-size / 4 + 1, -size / 2 - 3, -size / 3 + 2, -2);

    // 右砲身エッジ外側 (ベースリム - シャドウ側)
    p.stroke(140, 40, 180, 60);
    p.strokeWeight(2);
    p.line(size / 4, -size / 2 - 5, size / 3, 0);

    // 右砲身エッジ内側 (やや明るい - ベベル効果)
    p.stroke(200, 100, 255, 100);
    p.strokeWeight(1);
    p.line(size / 4 - 1, -size / 2 - 3, size / 3 - 2, -2);

    // === 7. 砲口グロー（チャージ/発射時は強化） ===
    const cannonCharging = getCannonCharging();
    const cannonActive = getCannonActive();

    const glowIntensity = (cannonCharging || cannonActive) ? 255 : 150;
    p.fill(255, 200, 255, glowIntensity);
    p.noStroke();
    const glowSize = (cannonCharging || cannonActive) ? 8 : 5;
    p.ellipse(-size / 4, -size / 2 - 5, glowSize, glowSize * 0.8);
    p.ellipse(size / 4, -size / 2 - 5, glowSize, glowSize * 0.8);

    // === 8. 三角キャノピー強化 - ガラス反射 ===
    // リム (フレーム)
    p.fill(180, 100, 200, 200);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -size / 6 - 2);
    p.vertex(-size / 8 - 2, size / 8 + 1);
    p.vertex(size / 8 + 2, size / 8 + 1);
    p.endShape(p.CLOSE);

    // ガラス本体
    p.fill(255, 150, 255);
    p.stroke(255, 200, 255);
    p.strokeWeight(1);
    p.beginShape();
    p.vertex(0, -size / 6);
    p.vertex(-size / 8, size / 8);
    p.vertex(size / 8, size / 8);
    p.endShape(p.CLOSE);

    // メイン反射 (左上)
    p.fill(255, 255, 255, 200);
    p.noStroke();
    p.ellipse(-size / 24, -size / 16, size / 14, size / 12);

    // サブ反射 (右下)
    p.fill(255, 220, 255, 100);
    p.ellipse(size / 40, size / 20, size / 18, size / 16);

    p.pop();
}

/**
 * applyPlayerTilt() - プレイヤー機体の傾き計算と適用
 * 移動方向に応じて機体を傾ける座標変換を実行
 * @param player - プレイヤーオブジェクト
 */
export function applyPlayerTilt(player: Player): void {
    const p = getP5();

    // 移動方向に応じて機体を傾ける
    const moveDirection = player.targetX - player.x;
    const maxTilt = 0.15; // 最大傾き角度（ラジアン、約8.6度）
    const tiltSpeed = 0.08; // 傾きの変化速度
    const tiltThreshold = 0.001; // この値以下は0とみなす

    // 現在の傾きを計算（スムーズに変化）
    if (Math.abs(moveDirection) > 1) {
        // 移動中：目標角度に向かって傾く
        const targetTilt = Math.sign(moveDirection) * maxTilt;
        const currentTilt = Math.atan2(Math.sin(player.tilt || 0), Math.cos(player.tilt || 0));
        player.tilt = currentTilt + (targetTilt - currentTilt) * tiltSpeed;
    } else {
        // 停止中：徐々に水平に戻る
        const currentTilt = player.tilt || 0;
        if (Math.abs(currentTilt) < tiltThreshold) {
            // 閾値以下は0にして計算をスキップ
            player.tilt = 0;
        } else {
            player.tilt = currentTilt * 0.85;
        }
    }

    // 機体を傾ける（傾きがある場合のみ）
    if (player.tilt) {
        p.rotate(player.tilt);
    }
}

/**
 * drawSideBoosters() - サイドバーニアの描画
 * 移動開始時に一瞬噴射するスラスターエフェクト
 * @param size - 機体サイズ
 * @param shipType - 機体タイプ（色を変更）
 */
function drawSideBoosters(size: number, shipType: string): void {
    const p = getP5();
    const leftTimer = getSideBoosterLeftTimer();
    const rightTimer = getSideBoosterRightTimer();

    if (leftTimer <= 0 && rightTimer <= 0) {
        return;
    }

    // 機体タイプに応じた色設定
    let boosterColor: number[];
    switch (shipType) {
        case 'bomber':
            boosterColor = [0, 200, 255]; // シアン
            break;
        case 'hunter':
            boosterColor = [255, 150, 0]; // オレンジ
            break;
        case 'lancer':
            boosterColor = [200, 50, 255]; // 紫
            break;
        default:
            boosterColor = [255, 255, 255];
    }

    const boosterMaxDuration = 12;
    const boosterY = size / 6; // 機体中央付近

    // 左サイドバーニア（右に移動時に噴射）
    if (leftTimer > 0) {
        const progress = leftTimer / boosterMaxDuration;
        const alpha = progress * 255;
        const flameLength = progress * 15;

        p.noStroke();

        // 外側グロー
        p.fill(boosterColor[0], boosterColor[1], boosterColor[2], alpha * 0.3);
        p.ellipse(-size / 2 - 2, boosterY, flameLength + 8, 10);

        // 中間炎
        p.fill(boosterColor[0], boosterColor[1], boosterColor[2], alpha * 0.6);
        p.ellipse(-size / 2 - 2, boosterY, flameLength + 4, 7);

        // 内側炎（明るい）
        p.fill(255, 255, 255, alpha * 0.8);
        p.ellipse(-size / 2 - 1, boosterY, flameLength, 4);
    }

    // 右サイドバーニア（左に移動時に噴射）
    if (rightTimer > 0) {
        const progress = rightTimer / boosterMaxDuration;
        const alpha = progress * 255;
        const flameLength = progress * 15;

        p.noStroke();

        // 外側グロー
        p.fill(boosterColor[0], boosterColor[1], boosterColor[2], alpha * 0.3);
        p.ellipse(size / 2 + 2, boosterY, flameLength + 8, 10);

        // 中間炎
        p.fill(boosterColor[0], boosterColor[1], boosterColor[2], alpha * 0.6);
        p.ellipse(size / 2 + 2, boosterY, flameLength + 4, 7);

        // 内側炎（明るい）
        p.fill(255, 255, 255, alpha * 0.8);
        p.ellipse(size / 2 + 1, boosterY, flameLength, 4);
    }
}

/**
 * drawPlayer() - プレイヤーの描画
 * 被弾中やゲームオーバー時は描画しない
 * 左右移動時に機体を傾けて動きを演出
 */
export function drawPlayer(): void {
    const p = getP5();
    // ゲーム状態と機体情報を取得
    const gameState = getGameState();
    const selectedShip = getSelectedShip();
    const player = getPlayer();

    // プレイヤーが存在しない、非アクティブ、または死亡演出中の場合は描画しない
    if (!player || !player.active || player.state?.name === 'Dying' || gameState === 'gameover') {
        return;
    }

    // Entry状態かどうかを判定
    const isEntryState = player.state?.name === 'Entry';
    const boosterAlpha = getBoosterAlpha();
    const boosterIntensity = boosterAlpha / 255;

    // 描画コンテキストを保存
    p.push();
    // プレイヤーの座標に移動
    p.translate(player.x, player.y);

    // 機体の傾きを適用
    applyPlayerTilt(player);

    // Entry状態時のブースターエフェクト（背景層：大きく広がる炎）
    if (isEntryState && boosterIntensity > 0) {
        const shipColor = getShipColor(selectedShip);
        drawBoosterFlame(boosterIntensity * 0.8, shipColor);
    }

    // エフェクト描画（シールド、パワーレベル）
    drawPlayerEffects(player.size);

    // 機体描画 - 選択された機体タイプに応じて描画
    switch (selectedShip) {
        case 'bomber':
            drawShipBomber(0, 0, player.size);
            break;
        case 'hunter':
            drawShipHunter(0, 0, player.size);
            break;
        case 'lancer':
            drawShipLancer(0, 0, player.size);
            break;
    }

    // Entry状態時のエンジン炎（前景層：機体別4層グラデーション）
    if (isEntryState && boosterIntensity > 0) {
        drawShipEngineFlames(selectedShip, boosterIntensity, player.size);
    }

    // サイドバーニアの描画（移動開始時に一瞬噴射）
    drawSideBoosters(player.size, selectedShip);

    // 描画コンテキストを復元
    p.pop();
}

/**
 * 機体タイプに応じたカラーを取得
 */
function getShipColor(shipType: string): readonly number[] {
    switch (shipType) {
        case 'bomber':
            return [0, 200, 255] as const;  // シアン
        case 'hunter':
            return [255, 150, 0] as const;  // オレンジ
        case 'lancer':
            return [200, 50, 255] as const; // 紫
        default:
            return [255, 255, 255] as const;
    }
}
